import { Manifest } from '../types';
import { getApiServer, appendAuthToken } from '../config';

declare const __APP_CONFIG__: any;

export interface WxStationBinaryRecord {
  id: string;
  name: string;
  lat: number;
  lon: number;
  elevFt: number | null;
  tempF: number;
  dewF: number | null;
  feelsF: number;
  wspdMph: number | null;
  wgstMph: number | null;
  wdirDeg: number | null;
  wdirCompass: string;
  obsAgeMin: number;
  rhPct: number | null;
  skyCode: number;
  wxCode: number;
  sourceType: 'ASOS/AWOS' | 'RAWS';
  conditionText: string;
  conditionIcon: string;
}

export interface WxStationMatch extends WxStationBinaryRecord {
  distanceMiles: number;
  elevationDiffFt: number | null;
  effectiveScoreMiles: number;
}

interface CachedShard {
  fetchedAtMs: number;
  generatedEpoch: number;
  stations: WxStationBinaryRecord[];
}

const COMPASS_16 = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
];

export function degreesToCompass(deg: number | null): string {
  if (deg === null || deg < 0 || !Number.isFinite(deg)) return '';
  const idx = Math.round(((deg % 360) + 360) % 360 / 22.5) % 16;
  return COMPASS_16[idx];
}

function haversineDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function resolveConditionFromCodes(skyCode: number, wxCode: number): { text: string; icon: string } {
  switch (wxCode) {
    case 5:
      return { text: 'Thunderstorm', icon: '⛈️' };
    case 7:
      return { text: 'Freezing Precip', icon: '🌨️' };
    case 4:
      return { text: 'Heavy Snow', icon: '❄️' };
    case 3:
      return { text: 'Snow', icon: '🌨️' };
    case 2:
      return { text: 'Heavy Rain', icon: '🌧️' };
    case 1:
      return { text: 'Rain', icon: '🌦️' };
    case 6:
      return { text: 'Fog / Mist', icon: '🌫️' };
  }
  switch (skyCode) {
    case 1:
      return { text: 'Clear', icon: '☀️' };
    case 2:
      return { text: 'Mostly Clear', icon: '🌤️' };
    case 3:
      return { text: 'Partly Cloudy', icon: '⛅' };
    case 4:
      return { text: 'Mostly Cloudy', icon: '🌥️' };
    case 5:
      return { text: 'Overcast', icon: '☁️' };
    case 6:
      return { text: 'Obscured Sky', icon: '🌫️' };
    default:
      return { text: 'Reported', icon: '⛅' };
  }
}

function decodeNullPaddedUtf8(bytes: Uint8Array): string {
  let end = 0;
  while (end < bytes.length && bytes[end] !== 0) {
    end++;
  }
  if (end === 0) return '';
  return new TextDecoder('utf-8').decode(bytes.subarray(0, end)).trim();
}

export class WxStationBinaryService {
  private static shardCache: Map<string, CachedShard> = new Map();
  private static inFlight: Map<string, Promise<WxStationBinaryRecord[] | null>> = new Map();
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  private static getShardConfig() {
    const cfg = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.wx_stations?.binary_shards) || {};
    return {
      zoom: Number(cfg.zoom ?? 4),
      maxSearchRadiusMiles: Number(cfg.max_search_radius_miles ?? 60.0),
      elevPenaltyMilesPer1000Ft: Number(cfg.elevation_penalty_miles_per_1000ft ?? 3.0)
    };
  }

  private static latLonToTileXY(lat: number, lon: number, zoom: number): { tx: number; ty: number } {
    const n = 1 << zoom;
    const clampedLat = Math.max(-85.0511, Math.min(85.0511, lat));
    const tx = Math.max(0, Math.min(n - 1, Math.floor(((lon + 180.0) / 360.0) * n)));
    const latRad = (clampedLat * Math.PI) / 180.0;
    const mercN = Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad));
    const ty = Math.max(0, Math.min(n - 1, Math.floor(((1.0 - mercN / Math.PI) / 2.0) * n)));
    return { tx, ty };
  }

  private static parseShardBuffer(buffer: ArrayBuffer): CachedShard | null {
    if (buffer.byteLength < 16) return null;
    const view = new DataView(buffer);
    const m0 = view.getUint8(0);
    const m1 = view.getUint8(1);
    const m2 = view.getUint8(2);
    const m3 = view.getUint8(3);
    // "GWXS" = 0x47, 0x57, 0x58, 0x53
    if (m0 !== 0x47 || m1 !== 0x57 || m2 !== 0x58 || m3 !== 0x53) {
      return null;
    }

    const count = view.getUint32(8, true);
    const generatedEpoch = view.getUint32(12, true);
    const headerBytes = 16;
    const recordBytes = 64;
    const availableCount = Math.min(count, Math.floor((buffer.byteLength - headerBytes) / recordBytes));

    const stations: WxStationBinaryRecord[] = [];
    const u8 = new Uint8Array(buffer);

    for (let i = 0; i < availableCount; i++) {
      const off = headerBytes + i * recordBytes;
      const lat = view.getFloat32(off, true);
      const lon = view.getFloat32(off + 4, true);
      const elevRaw = view.getInt16(off + 8, true);
      const tempX10 = view.getInt16(off + 10, true);
      if (tempX10 === -32768) continue;

      const dewX10 = view.getInt16(off + 12, true);
      const feelsX10 = view.getInt16(off + 14, true);
      const wspdRaw = view.getInt16(off + 16, true);
      const wgstRaw = view.getInt16(off + 18, true);
      const wdirRaw = view.getInt16(off + 20, true);
      const obsAgeMin = view.getInt16(off + 22, true);

      const rhRaw = view.getUint8(off + 24);
      const skyCode = view.getUint8(off + 25);
      const wxCode = view.getUint8(off + 26);
      const srcTypeRaw = view.getUint8(off + 27);

      const id = decodeNullPaddedUtf8(u8.subarray(off + 28, off + 36));
      const name = decodeNullPaddedUtf8(u8.subarray(off + 36, off + 64)) || id;

      const cond = resolveConditionFromCodes(skyCode, wxCode);
      const wdirDeg = wdirRaw >= 0 && wdirRaw <= 360 ? wdirRaw : null;

      stations.push({
        id,
        name,
        lat,
        lon,
        elevFt: elevRaw === -32768 ? null : elevRaw,
        tempF: tempX10 / 10.0,
        dewF: dewX10 === -32768 ? null : dewX10 / 10.0,
        feelsF: feelsX10 === -32768 ? tempX10 / 10.0 : feelsX10 / 10.0,
        wspdMph: wspdRaw >= 0 ? wspdRaw : null,
        wgstMph: wgstRaw >= 0 ? wgstRaw : null,
        wdirDeg,
        wdirCompass: degreesToCompass(wdirDeg),
        obsAgeMin: Math.max(0, obsAgeMin),
        rhPct: rhRaw <= 100 ? rhRaw : null,
        skyCode,
        wxCode,
        sourceType: srcTypeRaw === 2 ? 'RAWS' : 'ASOS/AWOS',
        conditionText: cond.text,
        conditionIcon: cond.icon
      });
    }

    return {
      fetchedAtMs: Date.now(),
      generatedEpoch,
      stations
    };
  }

  private static selectBestStation(
    stations: WxStationBinaryRecord[],
    lat: number,
    lon: number,
    poiElevFt?: number | null
  ): WxStationMatch | null {
    if (!stations || stations.length === 0) return null;
    const { maxSearchRadiusMiles, elevPenaltyMilesPer1000Ft } = this.getShardConfig();
    const hasPoiElev = typeof poiElevFt === 'number' && Number.isFinite(poiElevFt) && poiElevFt > -500;

    let bestMatch: WxStationMatch | null = null;

    for (const st of stations) {
      const distMiles = haversineDistanceMiles(lat, lon, st.lat, st.lon);
      if (distMiles > maxSearchRadiusMiles) continue;

      let elevDiffFt: number | null = null;
      let elevPenaltyMiles = 0;
      if (hasPoiElev && st.elevFt !== null) {
        elevDiffFt = Math.abs(st.elevFt - (poiElevFt as number));
        elevPenaltyMiles = (elevDiffFt / 1000.0) * elevPenaltyMilesPer1000Ft;
      }

      const effectiveScoreMiles = distMiles + elevPenaltyMiles;
      if (!bestMatch || effectiveScoreMiles < bestMatch.effectiveScoreMiles) {
        bestMatch = {
          ...st,
          distanceMiles: distMiles,
          elevationDiffFt: elevDiffFt,
          effectiveScoreMiles
        };
      }
    }

    return bestMatch;
  }

  public static getCachedBestStationForPoint(
    lat: number,
    lon: number,
    poiElevFt?: number | null
  ): WxStationMatch | null {
    const { zoom } = this.getShardConfig();
    const { tx, ty } = this.latLonToTileXY(lat, lon, zoom);
    const key = `${zoom}_${tx}_${ty}`;
    const cached = this.shardCache.get(key);
    if (!cached) return null;
    return this.selectBestStation(cached.stations, lat, lon, poiElevFt);
  }

  public static async getBestStationForPoint(
    lat: number,
    lon: number,
    poiElevFt?: number | null,
    manifest?: Manifest
  ): Promise<WxStationMatch | null> {
    const { zoom } = this.getShardConfig();
    const { tx, ty } = this.latLonToTileXY(lat, lon, zoom);
    const key = `${zoom}_${tx}_${ty}`;

    const cached = this.shardCache.get(key);
    if (cached && Date.now() - cached.fetchedAtMs < this.CACHE_TTL_MS) {
      return this.selectBestStation(cached.stations, lat, lon, poiElevFt);
    }

    let pending = this.inFlight.get(key);
    if (!pending) {
      pending = (async () => {
        try {
          const apiServer = getApiServer(manifest);
          const tileBase = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
          const cleanServer = apiServer.replace(/\/$/, '');
          const shardUrl = `${cleanServer}/${tileBase}/observed/wx_stations/z${zoom}/${tx}_${ty}.bin`
            .replace(/\/+/g, '/')
            .replace(':/', '://');
          const authUrl = appendAuthToken(shardUrl);
          const resp = await fetch(authUrl, { credentials: 'include' });
          if (!resp.ok) {
            return null;
          }
          const buf = await resp.arrayBuffer();
          const parsed = this.parseShardBuffer(buf);
          if (!parsed) return null;
          this.shardCache.set(key, parsed);
          return parsed.stations;
        } catch (err) {
          console.warn('[WxStationBinaryService] Failed to fetch Zoom-4 WX station shard:', err);
          return null;
        } finally {
          this.inFlight.delete(key);
        }
      })();
      this.inFlight.set(key, pending);
    }

    const stations = await pending;
    if (!stations) return null;
    return this.selectBestStation(stations, lat, lon, poiElevFt);
  }
}

